/**
 * MainMenu — the attract screen shown BEFORE the game boots.
 *   • Left  : the game logo (public/logo.png, with a styled word-mark fallback) + PLAY.
 *   • Right : a live "aquarium" preview — its OWN throwaway engine instance running
 *     attract mode (an immortal, fully-armed MK VI Battleship fighting an endless swarm)
 *     to show off what the late game looks like.
 *
 * PLAY hands control to GameCanvas, which then continues a save or runs onboarding.
 * The preview owns a private GameState, so nothing here can touch the player's real save.
 */
import { useEffect, useRef, useState } from "react";
import { RenderRoot } from "@/render/RenderRoot";
import { GameState } from "@/engine/GameState";
import { GameLoop } from "@/engine/GameLoop";
import { t, useLang } from "@/i18n";

interface Props {
  onPlay: () => void;
  onEnterHub: () => void;
  hubLocked: boolean; // true until the player reaches Mk II — the Hub button is greyed out
  username: string;
  online: number;
  onLogout: () => void;
}

const LOGO_SRC = `${import.meta.env.BASE_URL}logo.png`;

export default function MainMenu({ onPlay, onEnterHub, hubLocked, username, online, onLogout }: Props) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [logoOk, setLogoOk] = useState(true);
  useLang(); // re-render the labels when the language toggles

  // Boot a self-contained engine into the preview pane (mirrors GameCanvas, minus HUD/input).
  useEffect(() => {
    const mount = previewRef.current;
    if (!mount) return;
    let cancelled = false;
    let ready = false;
    const render = new RenderRoot();
    const state = new GameState();
    state.startAttract(); // immortal MK VI vs an endless swarm — plays itself
    let loop: GameLoop | null = null;
    render
      .mount(mount)
      .then(() => {
        if (cancelled) {
          render.destroy();
          return;
        }
        ready = true;
        loop = new GameLoop({
          update: (dt) => state.update(dt),
          render: (alpha) => {
            try {
              render.render(state, alpha);
            } catch (e) {
              console.error("[space-swarm] menu preview render error:", e);
              loop?.stop();
            }
          },
        });
        loop.start();
      })
      .catch((err) => console.error("[space-swarm] menu preview init failed:", err));
    return () => {
      cancelled = true;
      loop?.stop();
      if (ready) render.destroy(); // if not yet ready, the .then() above tears it down
    };
  }, []);

  return (
    <div className="main-menu">
      <div className="main-menu__left">
        <div className="main-menu__online">
          <span className="main-menu__online-dot" />
          {t("Players online")}: <b>{online}</b>
        </div>
        {logoOk ? (
          <img
            className="main-menu__logo"
            src={LOGO_SRC}
            alt="Space Adventura"
            onError={() => setLogoOk(false)}
          />
        ) : (
          <div className="main-menu__wordmark" role="img" aria-label="Space Adventura">
            <span className="main-menu__word main-menu__word--top">SPACE</span>
            <span className="main-menu__word main-menu__word--bot">ADVENTURA</span>
          </div>
        )}
        <button className="main-menu__play" onClick={onPlay} autoFocus>
          {t("▶  PLAY")}
        </button>
        <button
          className="main-menu__hub"
          type="button"
          onClick={onEnterHub}
          disabled={hubLocked}
          title={hubLocked ? t("Reach Mk II to join the Hub") : undefined}
        >
          {hubLocked ? "🔒" : "🌐"} {t("Enter Hub")}
        </button>
        {hubLocked && <div className="main-menu__hub-lock">{t("Reach Mk II to join the Hub")}</div>}
        <div className="main-menu__hub-beta">🧪 {t("Hub is in testing")}</div>
        <div className="main-menu__hint">{t("Mine · Upgrade · Conquer deep space")}</div>
        <div className="main-menu__account">
          {t("Signed in as")} <b>{username}</b>
          <button className="main-menu__logout" type="button" onClick={onLogout}>
            {t("Log out")}
          </button>
        </div>
      </div>

      <div className="main-menu__right">
        <div ref={previewRef} className="main-menu__canvas" />
        <div className="main-menu__preview-tag">
          <span className="main-menu__live-dot" />
          {t("LIVE — late-game preview")}
        </div>
      </div>
    </div>
  );
}
